// src/pages/ContactUs.js
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Container, Typography, Box, Grid, TextField, Button, Paper } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

const ContactUs = () => {
  const { t, language } = useLanguage();
  const isArabic = language === 'ar';
  
  // حالة النموذج
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    role: 'customer' // customer, driver, restaurant
  });
  
  // حالة إرسال النموذج
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  
  // تحديث بيانات النموذج
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // إرسال النموذج
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // محاكاة لعملية الإرسال (يمكن استبدالها بطلب API حقيقي)
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // إعادة تعيين النموذج بعد الإرسال الناجح
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        role: 'customer'
      });
      
      // إخفاء رسالة النجاح بعد 5 ثوانٍ
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }, 1500);
  };
  
  const containerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: isArabic ? 'Cairo, sans-serif' : 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    lineHeight: '1.6',
    direction: isArabic ? 'rtl' : 'ltr',
    textAlign: isArabic ? 'right' : 'left'
  };
  
  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };
  
  const inputStyle = {
    padding: '12px 15px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '16px',
    transition: 'border-color 0.3s',
    backgroundColor: '#f9f9f9'
  };
  
  const labelStyle = {
    fontWeight: '600',
    marginBottom: '5px',
    display: 'block',
    color: '#2c3e50'
  };
  
  const buttonStyle = {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    padding: '14px 20px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '10px'
  };
  
  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    padding: '25px',
    marginBottom: '30px'
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ 
        fontWeight: 'bold',
        textAlign: 'center',
        mb: 4
      }}>
        {t('contact_us')}
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 'bold',
              textAlign: language === 'ar' ? 'right' : 'left',
              mb: 3
            }}>
              {t('contact_form')}
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                required
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              <TextField
                fullWidth
                label={t('email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              <TextField
                fullWidth
                label={t('subject')}
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                margin="normal"
                required
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              <TextField
                fullWidth
                label={t('message')}
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                margin="normal"
                required
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{ mt: 3 }}
              >
                {t('send_message')}
              </Button>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 'bold',
              textAlign: language === 'ar' ? 'right' : 'left',
              mb: 3
            }}>
              {t('contact_info')}
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography>
                  {t('address')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography>
                  {t('phone')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography>
                  {t('contact_email')}
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ 
              fontWeight: 'bold',
              textAlign: language === 'ar' ? 'right' : 'left',
              mb: 2
            }}>
              {t('working_hours')}
            </Typography>
            <Typography paragraph>
              {t('working_hours_description')}
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ 
              fontWeight: 'bold',
              textAlign: language === 'ar' ? 'right' : 'left',
              mb: 2
            }}>
              {t('social_media')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" color="primary">
                Facebook
              </Button>
              <Button variant="outlined" color="primary">
                Twitter
              </Button>
              <Button variant="outlined" color="primary">
                Instagram
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
export default ContactUs;